import { ApplyActionTypes } from '@/store/apply/types';
import { Alert, AlertIcon, Button, ButtonGroup, Input, InputGroup, InputLeftElement, Stack } from '@chakra-ui/react';
import { faGbp } from '@fortawesome/pro-regular-svg-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Combobox,
  CustomFormControl,
  DateInput,
  getDateFromString,
  getStringFromDate,
  Icon,
  Modal,
  Select,
  CourseOptionWithMetadata,
} from 'shared-components/';
import { ApplyScholarship, EntryPoint, IApplyCourse, ScholarshipType, StudyLevel, StudyMode } from 'api-paths-cas';
import { ApplicationState } from '../../../../store';
import {
  createApplicantOffer,
  getApplicantCourseChange,
  getApplicantCourses,
  getApplicantIntakes,
  updateApplicantProgramme,
} from '../../../../store/apply/actions';
import { IDate } from '../../../../types';
import {
  aggregateCoursesByName,
  findCourseIdForStaffSelection,
  formatModeLabel,
  formatModesForDisplay,
  formatCampusesForDisplay,
  getAutoSelectedSingleOptionValue,
} from '../../../organisms/ApplicationDrawer/utils/courseFiltering';
import { CourseCombobox } from '../../../organisms/ApplicationDrawer/components/CourseCombobox';

export default function ProgrammeDetailsModal({
  onClose,
  forceCreate,
}: {
  onClose: () => void;
  forceCreate?: boolean;
}): React.JSX.Element {
  const offer = useSelector((state: ApplicationState) => state.apply.offer);
  const offerCreated = useSelector((state: ApplicationState) => state.apply.offerCreated);
  const {
    applicantIntakes: intakes = [],
    courses = [],
    loadingCourses = false,
  } = useSelector((state: ApplicationState) => state.apply);
  const scholarships = useSelector((state: ApplicationState) => state.applyConfig.applyConfig.scholarships);
  const courseDetails = useSelector((state: ApplicationState) => state.apply.courseDetails);
  const { userId, applicationId, uni } = useParams();
  const [intakeSelected, setIntakeSelected] = useState<string>();
  const [studyLevel, setStudyLevel] = useState<StudyLevel>();
  const [courseName, setCourseName] = useState<string>();
  const [mode, setMode] = useState<StudyMode>();
  const [campus, setCampus] = useState<string>();
  const [entryPoint, setEntryPoint] = useState<EntryPoint>();
  const [startDate, setStartDate] = useState<IDate>();
  const [endDate, setEndDate] = useState<IDate>();
  const [fee, setFee] = useState<number>();
  const [feeTotal, setFeeTotal] = useState<number>();
  const [selectedScholarships, setSelectedScholarships] = useState<ApplyScholarship[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Derive study levels from the intake object when available so the dropdown is usable immediately
  // after intake selection. Falls back to deriving from courses if intake study levels are not set.
  const studyLevelOptions = useMemo(() => {
    if (!intakeSelected) return [];
    const selectedIntake = intakes.find(i => i.value === intakeSelected);
    if (selectedIntake?.studyLevels?.length) {
      return selectedIntake.studyLevels
        .map(level => ({ value: level, label: level }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    // Fallback: derive from courses once loaded
    const levelSet = new Set<StudyLevel>();
    for (const course of courses) {
      if (course.intakeId === intakeSelected) {
        levelSet.add(course.level as StudyLevel);
      }
    }
    return Array.from(levelSet)
      .map(level => ({ value: level, label: level }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [intakes, courses, intakeSelected]);

  // Filter courses by selected intake and study level
  const filteredCourses = useMemo(() => {
    if (!intakeSelected || !studyLevel) return [];
    return courses.filter(course => course.intakeId === intakeSelected && course.level === studyLevel);
  }, [courses, intakeSelected, studyLevel]);

  // Aggregate courses by name for the Programme dropdown with metadata
  const aggregatedCourses: CourseOptionWithMetadata[] = useMemo(() => {
    const aggregated = aggregateCoursesByName(filteredCourses);
    return Array.from(aggregated.values())
      .map(course => ({
        value: course.name,
        label: course.name,
        metadata: {
          modes: formatModesForDisplay(Array.from(course.modes)),
          campuses: formatCampusesForDisplay(Array.from(course.campuses)),
        },
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredCourses]);

  // Get available modes for selected course name
  const modeOptions = useMemo(() => {
    if (!courseName) return [];
    const modesSet = new Set<StudyMode>();
    for (const course of filteredCourses) {
      if (course.name === courseName) {
        modesSet.add(course.mode);
      }
    }
    return Array.from(modesSet)
      .map(m => ({ value: m, label: formatModeLabel(m) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredCourses, courseName]);

  // Get available campuses for selected course name and mode
  const campusOptions = useMemo(() => {
    if (!courseName || !mode) return [];
    const campusSet = new Set<string>();
    for (const course of filteredCourses) {
      if (course.name === courseName && course.mode === mode) {
        campusSet.add(course.campus);
      }
    }
    return Array.from(campusSet)
      .map(c => ({ value: c, label: c }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredCourses, courseName, mode]);

  // Get available entry points for selected course name, mode, and campus
  const entryPointOptions = useMemo(() => {
    if (!courseName || !mode || !campus) return [];
    const entryPointSet = new Set<EntryPoint>();
    for (const course of filteredCourses) {
      if (course.name === courseName && course.mode === mode && course.campus === campus) {
        entryPointSet.add(course.entryPoint);
      }
    }
    return Array.from(entryPointSet)
      .map(ep => ({ value: ep, label: ep }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredCourses, courseName, mode, campus]);

  // Resolve the courseId from all selections
  const resolvedCourseId = useMemo(() => {
    if (!intakeSelected || !courseName || !mode || !campus || !entryPoint) return null;
    return findCourseIdForStaffSelection(
      courses as IApplyCourse[],
      intakeSelected,
      courseName,
      mode,
      campus,
      entryPoint,
    );
  }, [courses, intakeSelected, courseName, mode, campus, entryPoint]);

  // Auto-select single options using the shared helper
  useEffect(() => {
    const autoMode = getAutoSelectedSingleOptionValue(!!courseName, modeOptions, mode);
    if (autoMode) {
      setMode(autoMode as StudyMode);
    }
  }, [modeOptions, courseName, mode]);

  useEffect(() => {
    const autoCampus = getAutoSelectedSingleOptionValue(!!mode, campusOptions, campus);
    if (autoCampus) {
      setCampus(autoCampus);
    }
  }, [campusOptions, mode, campus]);

  useEffect(() => {
    const autoEntryPoint = getAutoSelectedSingleOptionValue(!!campus, entryPointOptions, entryPoint);
    if (autoEntryPoint) {
      setEntryPoint(autoEntryPoint as EntryPoint);
    }
  }, [entryPointOptions, campus, entryPoint]);

  useEffect(() => {
    dispatch(getApplicantIntakes(uni));
  }, [dispatch, uni]);

  useEffect(() => {
    // when new offer is added
    if (!offer?.id || !offerCreated) return;
    onClose();
    dispatch({ type: ApplyActionTypes.RESET_OFFER_CREATED });
    navigate(`/application/${uni}/${userId}/${applicationId}/offers/${offer?.id}`);
  }, [dispatch, navigate, onClose, offer?.id, offerCreated, uni, userId, applicationId]);

  const isNew = useMemo(() => {
    if (!resolvedCourseId) return undefined;

    // Check if fields that trigger a new offer have changed
    const intakeChanged = intakeSelected !== offer?.intakeId;
    const courseNameChanged = courseName !== offer?.programme?.courseName;
    const modeChanged = mode !== offer?.programme?.mode;
    const entryPointChanged = entryPoint !== offer?.programme?.entryPoint;

    // Create new offer if intake, course name, mode, or entry point changed
    // Campus changes do NOT trigger a new offer
    return intakeChanged || courseNameChanged || modeChanged || entryPointChanged;
  }, [
    intakeSelected,
    offer?.intakeId,
    courseName,
    offer?.programme?.courseName,
    mode,
    offer?.programme?.mode,
    entryPoint,
    offer?.programme?.entryPoint,
    resolvedCourseId,
  ]);

  useEffect(() => {
    dispatch(getApplicantCourses(userId, uni, applicationId));
  }, [dispatch, userId, uni, applicationId]);

  useEffect(() => {
    if (intakeSelected) {
      dispatch(getApplicantCourses(userId, uni, applicationId, intakeSelected));
    }
  }, [dispatch, intakeSelected, userId, uni, applicationId]);

  useEffect(() => {
    if (isNew && resolvedCourseId) {
      dispatch(getApplicantCourseChange(userId, uni, applicationId, intakeSelected, resolvedCourseId));
    }
  }, [dispatch, isNew, resolvedCourseId, userId, uni, applicationId, intakeSelected]);

  useEffect(() => {
    // set default values when offer is fetched - only run once on mount
    // Guard against empty courses to ensure study level lookup can succeed
    if (offer && !hasInitialized && !forceCreate && courses.length > 0) {
      setIntakeSelected(offer.intakeId);
      // Look up the study level from the course data
      const matchingCourse = courses.find(course => course.id === offer.programme?.courseId);
      if (matchingCourse) {
        setStudyLevel(matchingCourse.level as StudyLevel);
      }
      setCourseName(offer.programme?.courseName);
      setMode(offer.programme?.mode);
      setCampus(offer.pendingChanges?.programme?.campus ?? offer.programme?.campus);
      setEntryPoint(offer.pendingChanges?.programme?.entryPoint ?? offer.programme?.entryPoint);
      setFee(offer.pendingChanges?.programme?.fee ?? offer.programme?.fee);
      setFeeTotal(offer.pendingChanges?.programme?.feeTotal ?? offer.programme?.feeTotal);
      setStartDate(getDateFromString(offer.pendingChanges?.programme?.startDate ?? offer?.programme?.startDate));
      setEndDate(getDateFromString(offer.pendingChanges?.programme?.endDate ?? offer?.programme?.endDate));
      setSelectedScholarships(offer.pendingChanges?.programme?.scholarships ?? offer.programme?.scholarships ?? []);
      setHasInitialized(true);
    } else if ((forceCreate || isNew) && resolvedCourseId && courseDetails) {
      setStartDate(getDateFromString(courseDetails.programme?.startDate));
      setEndDate(getDateFromString(courseDetails.programme?.endDate));
      // Only set entryPoint from courseDetails if user hasn't selected one yet
      // This prevents overwriting user's selection when resolvedCourseId changes
      if (!entryPoint) {
        setEntryPoint(courseDetails.programme?.entryPoint);
      }
      setFee(courseDetails.programme?.fee);
      setFeeTotal(courseDetails.programme?.feeTotal);
    }
  }, [offer, offer?.id, isNew, courseDetails, resolvedCourseId, entryPoint, hasInitialized, forceCreate, courses]);

  // Handle intake change - reset dependent fields
  const handleIntakeChange = (event: React.FormEvent<HTMLSelectElement>): void => {
    setIntakeSelected(event.currentTarget.value);
    setStudyLevel(undefined);
    setCourseName(undefined);
    setMode(undefined);
    setCampus(undefined);
    setEntryPoint(undefined);
  };

  // Handle study level change - reset course name and dependent fields
  const handleStudyLevelChange = (event: React.FormEvent<HTMLSelectElement>): void => {
    setStudyLevel(event.currentTarget.value as StudyLevel);
    setCourseName(undefined);
    setMode(undefined);
    setCampus(undefined);
    setEntryPoint(undefined);
  };

  // Handle course name change - reset dependent fields
  const handleCourseNameChange = (value: string): void => {
    setCourseName(value);
    setMode(undefined);
    setCampus(undefined);
    setEntryPoint(undefined);
  };

  // Handle mode change - only reset campus/entryPoint if they're no longer valid for the new mode
  const handleModeChange = (event: React.FormEvent<HTMLSelectElement>): void => {
    const newMode = event.currentTarget.value as StudyMode;
    setMode(newMode);

    if (courseName) {
      // Check if current campus is still available for the new mode
      const campusStillValid =
        campus &&
        filteredCourses.some(
          course => course.name === courseName && course.mode === newMode && course.campus === campus,
        );

      if (!campusStillValid) {
        setCampus(undefined);
        setEntryPoint(undefined);
      } else if (entryPoint) {
        // Campus is valid, check if entry point is still valid
        const entryPointStillValid = filteredCourses.some(
          course =>
            course.name === courseName &&
            course.mode === newMode &&
            course.campus === campus &&
            course.entryPoint === entryPoint,
        );
        if (!entryPointStillValid) {
          setEntryPoint(undefined);
        }
      }
    }
  };

  // Handle campus change - only reset entry point if it's no longer valid for the new campus
  const handleCampusChange = (event: React.FormEvent<HTMLSelectElement>): void => {
    const newCampus = event.currentTarget.value;
    setCampus(newCampus);

    // Check if current entry point is still available at the new campus
    if (entryPoint && courseName && mode) {
      const entryPointStillValid = filteredCourses.some(
        course =>
          course.name === courseName &&
          course.mode === mode &&
          course.campus === newCampus &&
          course.entryPoint === entryPoint,
      );
      if (!entryPointStillValid) {
        setEntryPoint(undefined);
      }
    }
  };

  // Handle entry year change
  const handleEntryPointChange = (event: React.FormEvent<HTMLSelectElement>): void => {
    setEntryPoint(event.currentTarget.value as EntryPoint);
  };

  const handleSave = (): void => {
    const startDateString = getStringFromDate(startDate);
    const endDateString = getStringFromDate(endDate);
    if (forceCreate || isNew) {
      dispatch(
        createApplicantOffer(userId, uni, applicationId, {
          intakeId: intakeSelected,
          courseId: resolvedCourseId,
          startDate: startDateString,
          endDate: endDateString,
          fee,
          feeTotal,
          entryPoint,
          scholarships: selectedScholarships,
        }),
      );
    } else {
      dispatch(
        updateApplicantProgramme(userId, uni, offer.id, {
          entryPoint,
          fee,
          feeTotal,
          startDate: startDateString,
          endDate: endDateString,
          scholarships: selectedScholarships,
          campus,
        }),
      );
      onClose();
    }
  };

  // Check if all required fields for update are present
  const hasRequiredUpdateFields = entryPoint && fee !== undefined && startDate && endDate;

  // Disable save if:
  // - Creating new offer (forceCreate or isNew is true) and no resolved course ID
  // - Updating existing offer but required fields are missing (would fail backend validation)
  // - isNew is undefined (resolvedCourseId is null, meaning incomplete selection)
  const isSaveDisabled = forceCreate || isNew ? !resolvedCourseId : isNew === undefined || !hasRequiredUpdateFields;

  return (
    <Modal
      id="programmeDetails"
      show={true}
      title={forceCreate ? 'Create programme' : 'Edit programme'}
      onClose={onClose}
      footer={
        <ButtonGroup size="lg" spacing={3}>
          <Button data-selector="programmeDetails-modal-close" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-selector="programmeDetails-modal-save"
            variant="solid"
            onClick={handleSave}
            isDisabled={isSaveDisabled}
          >
            {getButtonLabel(forceCreate, isNew)}
          </Button>
        </ButtonGroup>
      }
    >
      <Stack gap={6}>
        <Select
          id="intake"
          name="intake"
          label="Intake"
          size="lg"
          options={intakes}
          onChange={handleIntakeChange}
          value={intakeSelected || ''}
        />
        <Select
          id="studyLevel"
          name="studyLevel"
          label="Study level"
          size="lg"
          options={studyLevelOptions}
          onChange={handleStudyLevelChange}
          value={studyLevel || ''}
          isDisabled={!intakeSelected}
          placeHolder="Select a study level"
        />
        <CourseCombobox
          id="programme"
          name="programme"
          options={aggregatedCourses}
          onChange={handleCourseNameChange}
          value={courseName || ''}
          isDisabled={!intakeSelected || !studyLevel || loadingCourses}
          isLoading={!!intakeSelected && loadingCourses}
          placeholder={!!intakeSelected && loadingCourses ? 'Loading courses for selected intake...' : 'Search courses'}
        />
        {courseName && (
          <>
            <Select
              id="mode"
              name="mode"
              label="Mode"
              size="lg"
              options={modeOptions}
              onChange={handleModeChange}
              value={mode || ''}
              isDisabled={modeOptions.length === 0 || loadingCourses}
              isLoading={loadingCourses}
            />
            <Select
              id="campus"
              name="campus"
              label="Campus"
              size="lg"
              options={campusOptions}
              onChange={handleCampusChange}
              value={campus || ''}
              isDisabled={!mode || campusOptions.length === 0 || loadingCourses}
              isLoading={loadingCourses}
            />
            <Select
              id="entryPoint"
              name="entryPoint"
              label="Entry year"
              size="lg"
              options={entryPointOptions}
              onChange={handleEntryPointChange}
              value={entryPoint || ''}
              isDisabled={!campus || entryPointOptions.length === 0 || loadingCourses}
              isLoading={loadingCourses}
            />
            {isNew && !forceCreate && (
              <Alert size="sm" status="info" mt={2} colorScheme="cyan">
                <AlertIcon />A new draft offer will be created for this programme
              </Alert>
            )}
            <DateInput
              label="Start date"
              value={startDate}
              onChangeDD={e => setStartDate({ ...startDate, DD: e.currentTarget.value })}
              onChangeYY={e => setStartDate({ ...startDate, YYYY: e.currentTarget.value })}
              onChangeMM={e => setStartDate({ ...startDate, MM: e.currentTarget.value })}
            />
            <DateInput
              label="End date"
              value={endDate}
              onChangeDD={e => setEndDate({ ...endDate, DD: e.currentTarget.value })}
              onChangeYY={e => setEndDate({ ...endDate, YYYY: e.currentTarget.value })}
              onChangeMM={e => setEndDate({ ...endDate, MM: e.currentTarget.value })}
            />
            <CustomFormControl
              id={`course-fee-${entryPoint || 'default'}`}
              label={`Course fee${entryPoint ? ` (${entryPoint})` : ''}`}
            >
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <Icon icon={faGbp} />
                </InputLeftElement>
                <Input type="number" value={fee} onChange={e => setFee(Number(e.target.value))} />
              </InputGroup>
            </CustomFormControl>
            <CustomFormControl id={`course-fee-total`} label={`Total course fees`}>
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <Icon icon={faGbp} />
                </InputLeftElement>
                <Input type="number" value={feeTotal} onChange={e => setFeeTotal(Number(e.target.value))} />
              </InputGroup>
            </CustomFormControl>
            <Combobox
              mb={6}
              label="Scholarship"
              id="scholarship"
              size="lg"
              isMulti
              options={scholarships.map(scholarship => ({
                label: getScholarshipLabel(scholarship),
                value: getScholarshipId(scholarship),
              }))}
              onChange={(selectedOptions: Array<{ value: string; label: string }>) => {
                const options = selectedOptions;
                const selected = scholarships.filter(scholarship =>
                  options.some(option => option.value === getScholarshipId(scholarship)),
                );
                setSelectedScholarships(selected);
              }}
              value={selectedScholarships.map(s => getScholarshipId(s))}
            />
          </>
        )}
      </Stack>
    </Modal>
  );
}

const getButtonLabel = (forceCreate?: boolean, isNew?: boolean): string => {
  if (forceCreate) return 'Create programme';
  if (isNew) return 'Create as new programme';
  return 'Save changes';
};

const getScholarshipId = (scholarship: ApplyScholarship): string => {
  if (scholarship.type === ScholarshipType.PERCENTAGE) {
    return `${scholarship.name}-${scholarship.percentage}`;
  }
  return `${scholarship.name}-${scholarship.amount}`;
};

const getScholarshipLabel = (scholarship: ApplyScholarship): string => {
  if (scholarship.type === ScholarshipType.PERCENTAGE) {
    return `${scholarship.percentage}% ${scholarship.name}`;
  }
  return `£${scholarship?.amount?.toLocaleString() ?? '0'} ${scholarship.name}`;
};
